import React, { Fragment, useEffect } from "react";
import { Link } from "react-router-dom";

import Header from "../../../components/Header/Header";
import PageTitle from "../../../components/pagetitle/PageTitle";
import Footer from "../../../components/footer/Footer";
import Scrollbar from "../../../components/scrollbar/scrollbar";

import "./StaticTimingAnalysis.css";
import "./PageTheme.css";
import serviceImage from "../../../images/about/testing.jpg";

type Block = { title?: string; text?: string; lines?: string[]; raw: string };

const config = {
  "title": "Static Timing Analysis (STA)",
  "category": "Advanced Design & Implementation",
  "visual": "timing",
  "folder": "StaticTimingAnalysis",
  "sections": [
    [
      "Static Timing Analysis (STA)\nEngineering Timing Confidence for Reliable Silicon\nStatic Timing Analysis is a critical sign-off discipline that determines whether semiconductor designs can reliably operate at their intended performance across all operating conditions. Achieving timing closure demands deep expertise in timing methodologies, design optimization, constraint management, and implementation analysis to ensure predictable silicon performance.\n\nDigicomm Semiconductor delivers specialized Static Timing Analysis (STA) capabilities that help engineering teams identify timing bottlenecks, optimize critical paths, validate design integrity, and achieve sign-off confidence. Through disciplined engineering practices and collaborative execution, we support reliable timing closure that enables successful silicon realization across advanced semiconductor technologies.\n\nPrimary CTA\nDiscuss Your STA Requirements"
    ],
    [
      "Capability Overview\nStatic Timing Analysis (STA) is a critical sign-off discipline that validates whether a semiconductor design can reliably meet its timing requirements across multiple operating conditions without relying on dynamic simulation. It provides the engineering confidence needed to verify timing integrity, identify critical path violations, optimize performance, and ensure that designs are prepared for successful implementation and silicon realization.\n\nDigicomm Semiconductor delivers specialized Static Timing Analysis (STA) capabilities that support the complete timing closure process. From constraint development and timing analysis to optimization support, multi-mode multi-corner (MMMC) validation, and sign-off readiness, our engineering teams work collaboratively with customer organizations to improve timing convergence, strengthen design integrity, and enable predictable silicon performance."
    ],
    [
      "Why It Matters\nAs semiconductor technologies continue to advance, achieving timing closure has become one of the most critical challenges in successful silicon implementation. Increasing design complexity, higher operating frequencies, advanced process nodes, and multiple operating scenarios demand comprehensive timing analysis to ensure that every signal reaches its destination within the required time constraints.\n\nStatic Timing Analysis provides the engineering confidence needed to identify timing violations early, optimize critical paths, validate implementation quality, and prepare designs for sign-off. A disciplined STA methodology minimizes implementation risk, strengthens design integrity, and contributes directly to predictable, high-performance silicon.\n\nKey Outcomes:\nAchieves Reliable Timing Closure\nValidate and optimize timing across all operating conditions to support successful implementation and sign-off.\n\nStrengthens Design Integrity\nIdentify and resolve setup, hold, and timing constraint violations before fabrication.\n\nOptimizes Performance\nImprove timing convergence and critical path performance to achieve intended operating frequencies.\n\nReduces Implementation Risk\nDetect timing-related issues early, minimizing costly engineering iterations and project delays.\n\nBuilds Sign-off Confidence\nSupport implementation readiness through comprehensive timing validation and disciplined sign-off practices.\n\nContributes to Reliable Silicon\nEnable predictable silicon performance through robust timing analysis, engineering precision, and disciplined execution."
    ],
    [
      "Static Timing Analysis Engineering Scope\nTiming Constraint Development\nDevelop and validate comprehensive timing constraints that accurately represent design intent and provide a reliable foundation for timing analysis and implementation.\n\nStatic Timing Analysis\nPerform detailed timing analysis to evaluate setup, hold, recovery, removal, and pulse width requirements across multiple operating conditions.\n\nMulti-Mode Multi-Corner (MMMC) Analysis\nValidate timing performance across multiple functional modes, process corners, voltage conditions, and temperature variations to ensure robust design behavior.\n\nTiming Closure Support\nIdentify critical timing paths, analyze violations, and collaborate with implementation teams to achieve efficient timing convergence and sign-off readiness.\n\nTiming Optimization\nSupport engineering optimization activities that improve timing performance while maintaining power, area, and overall implementation objectives.\n\nClock Timing Analysis\nAnalyze clock distribution networks, clock uncertainty, skew, and latency to strengthen timing integrity across the design.\n\nSign-off Timing Validation\nPerform comprehensive timing validation and quality assessments to establish implementation confidence before tapeout.\n\nEngineering Collaboration\nWork closely with Physical Design, Design, Verification, and DFT teams to resolve timing challenges through coordinated engineering execution."
    ],
    [
      "Key Benefits\nSuccessful Static Timing Analysis extends beyond identifying timing violations. It provides the engineering confidence needed to achieve timing closure, strengthen implementation quality, and prepare complex semiconductor designs for successful sign-off. Digicomm's STA capabilities help organizations optimize timing performance, reduce implementation risk, and enable predictable silicon outcomes through disciplined engineering execution.\n\nAccelerated Timing Closure\nIdentify, analyze, and resolve timing challenges efficiently to improve convergence and support predictable implementation schedules.\n\nImproved Design Integrity\nValidate timing behavior across multiple operating conditions to strengthen design robustness and implementation quality.\n\nOptimized Performance\nImprove critical path performance and timing margins to help designs achieve their intended operating frequencies.\n\nReduced Implementation Risk\nDetect timing issues early and support proactive resolution, minimizing costly design iterations and project delays.\n\nGreater Sign-off Confidence\nApply comprehensive timing validation methodologies that improve implementation readiness before tapeout.\n\nPredictable Silicon Performance\nEnable reliable silicon operation through disciplined timing analysis, optimization, and engineering collaboration."
    ],
    [
      "Technology Expertise\nAchieving reliable timing closure requires more than comprehensive timing analysis—it demands expertise across advanced sign-off methodologies, process variation modeling, optimization strategies, and implementation-aware engineering practices. Digicomm's Static Timing Analysis teams work across modern STA methodologies and industry-standard implementation environments to support robust timing convergence and predictable silicon performance.\n\nSTA Methodologies\nStatic Timing Analysis (STA)\nMulti-Mode Multi-Corner (MMMC) Analysis\nTiming Constraint Development\nTiming Sign-off Methodologies\nTiming Closure Strategies\n\nTiming Analysis & Sign-off\nSetup & Hold Analysis\nRecovery & Removal Checks\nClock Domain Analysis\nClock Skew & Latency Analysis\nTiming Report Analysis\nSign-off Validation\n\nOptimization & Variation Analysis\nOn-Chip Variation (OCV)\nAdvanced On-Chip Variation (AOCV)\nParametric On-Chip Variation (POCV)\nSignal Integrity (SI) Aware Timing\nCrosstalk Analysis Support\nTiming Optimization\n\nImplementation Integration\nPhysical Design Collaboration\nClock Tree Synthesis (CTS) Support\nECO Timing Validation\nTiming Closure Support\nImplementation Readiness\n\nEDA Environment\n(Include only platforms actively used by Digicomm)\nSynopsys PrimeTime\nCadence Tempus\nSiemens EDA"
    ],
    [
      "Why Digicomm\nStatic Timing Analysis is more than a sign-off activity—it is a critical engineering discipline that influences implementation quality, silicon performance, and project success. Achieving reliable timing closure requires deep technical expertise, disciplined execution, and close collaboration across design and implementation teams. Digicomm combines specialized STA capabilities with structured engineering practices to help customers navigate complex timing challenges with confidence.\n\nWhat Sets Us Apart\nSpecialized STA Expertise\nDedicated engineering teams experienced in advanced timing analysis, timing closure methodologies, sign-off validation, and implementation support across complex semiconductor programs.\n\nDisciplined Timing Closure\nStructured engineering methodologies focused on constraint accuracy, timing convergence, implementation quality, and predictable sign-off outcomes.\n\nImplementation-Centric Collaboration\nSTA engineers who work closely with Physical Design, RTL Design, Verification, and DFT teams to resolve timing challenges through coordinated engineering execution.\n\nScalable Engineering Support\nFlexible engagement models that adapt to evolving project requirements, technology nodes, implementation complexity, and delivery schedules.\n\nCommitment to Reliable Silicon\nA quality-driven engineering approach focused on strengthening timing integrity, implementation readiness, and dependable silicon performance through disciplined execution."
    ],
    [
      "Related Engineering Capabilities:\nStatic Timing Analysis (STA)\nComprehensive timing analysis and optimization that supports timing closure, implementation quality, and predictable silicon performance.\nLearn More →\n\nFPGA Engineering\nFPGA architecture, implementation, prototyping, and validation supporting accelerated hardware development and functional evaluation.\nLearn More →\n\nRelated Core Engineering Services:\nASIC & SoC Design\nDevelop robust digital architectures that integrate seamlessly with analog and mixed-signal subsystems.\nLearn More →\n\nPhysical Design\nSupport optimized implementation, custom layout integration, and manufacturable silicon through disciplined physical design practices.\nLearn More →\n\nRelated Engineering Engagement Models\nEngineering Consulting\nAccess specialized analog engineering expertise to address complex technical challenges and accelerate development programs.\nLearn More →\n\nDedicated Engineering Teams\nExtend your engineering organization with experienced analog and mixed-signal specialists who integrate seamlessly into ongoing semiconductor programs.\nLearn More →"
    ],
    [
      "Let's Achieve Timing Closure with Confidence\n\nSuccessful timing closure demands more than advanced analysis tools—it requires experienced engineering, disciplined execution, and close collaboration throughout the implementation journey.\n\nWhether you're resolving complex timing challenges, accelerating sign-off, or strengthening implementation confidence for advanced semiconductor designs, Digicomm Semiconductor is ready to support your team with specialized Static Timing Analysis expertise, collaborative engineering, and dependable execution.\n\nPrimary CTA\nDiscuss Your STA Requirements"
    ]
  ]
} as const;

const splitBlocks = (raw: string): string[] =>
  raw.split(/\n\s*\n/g).map((x) => x.trim()).filter(Boolean);

const asBlock = (raw: string): Block => {
  const lines = raw.split("\n").map((x) => x.trim()).filter(Boolean);
  if (lines.length >= 2 && lines[0].length <= 74) {
    const rest = lines.slice(1);
    const listLike = rest.length >= 3 && rest.every((x) => x.length < 78);
    if (listLike) return { title: lines[0], lines: rest, raw };
    return { title: lines[0], text: rest.join(" "), raw };
  }
  if (lines.length === 1) return { text: lines[0], raw };
  return { text: lines.join(" "), raw };
};

const getHero = (section: readonly string[], pageTitle: string) => {
  const merged = section.join("\n\n");
  const blocks = splitBlocks(merged);
  const filtered = blocks.filter((x) => x !== "Primary CTA");
  if (filtered[0] === pageTitle) filtered.shift();

  const first = filtered.shift() || pageTitle;
  const firstLines = first.split("\n").map((x) => x.trim()).filter(Boolean);
  const headline = firstLines[0] || pageTitle;
  const body: string[] = [];

  if (firstLines.length > 1) body.push(firstLines.slice(1).join(" "));
  filtered.forEach((b) => body.push(b));

  let cta = "";
  if (body.length && body[body.length - 1].length < 85) cta = body.pop() || "";
  return { headline, body, cta };
};

const Arrow = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12h13M13 7l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const EngineeringVisual: React.FC = () => (
  <div className="ses-visual service-engineering-image">
    <img src={serviceImage} alt="Signal waveform measurements and semiconductor testing equipment" />
  </div>
);

const ContentPanel: React.FC<{ raw: string; index: number }> = ({ raw, index }) => {
  const blocks = splitBlocks(raw).filter((x) => x !== "Primary CTA");
  return (
    <div className="ses-block-grid">
      {blocks.map((rawBlock, i) => {
        const block = asBlock(rawBlock);
        const long = rawBlock.length > 430;
        const labelOnly =
          !block.title &&
          !!block.text &&
          block.text.length < 80 &&
          (/[:：]$/.test(block.text) || i === 0);

        return (
          <article
            className={`ses-block ${long ? "ses-block--wide" : ""} ${labelOnly ? "ses-block--label" : ""}`}
            key={`${index}-${i}`}
          >
            {block.title && <h3>{block.title}</h3>}
            {block.text && (labelOnly ? <h3>{block.text}</h3> : <p>{block.text}</p>)}
            {block.lines && <ul>{block.lines.map((line) => <li key={line}>{line}</li>)}</ul>}
          </article>
        );
      })}
    </div>
  );
};

const RelatedPanel: React.FC<{ raw: string }> = ({ raw }) => {
  const blocks = splitBlocks(raw);
  const routeMap: Record<string, string> = {
    "Analog & Mixed Signal Design & Layout": "/services/analog-mixed-signal",
    "Analog & Mixed Signal": "/services/analog-mixed-signal",
    "Static Timing Analysis (STA)": "/services/static-timing-analysis",
    "FPGA Engineering": "/services/fpga-engineering",
    "Embedded Software Systems": "/services/embedded-software-systems",
    "Hardware Platform Engineering": "/services/hardware-platform-engineering",
    "Networking Solutions": "/services/networking-solutions",
    "Silicon Validation": "/services/silicon-validation",
    "EDA & Design Automation": "/services/eda-design-automation",
    "Product Engineering": "/services/product-engineering",
    "ASIC & SoC Design": "/services/asic-soc-design",
    "Physical Design": "/services/physical-design",
    "Verification Services": "/services/verification",
  };

  const cards: React.ReactNode[] = [];
  let group = "";

  blocks.forEach((b, i) => {
    if (/^Related .*:$/.test(b.trim())) {
      group = b.trim();
      return;
    }
    if (b === "Learn More →") return;

    const lines = b.split("\n").map((x) => x.trim()).filter(Boolean);
    const title = lines[0];
    const desc = lines.slice(1).join(" ");

    if (routeMap[title]) {
      cards.push(
        <Link className="ses-related-card" to={routeMap[title]} key={`${title}-${i}`}>
          <span>{group}</span>
          <h3>{title}</h3>
          {desc && <p>{desc}</p>}
          <em>Learn More <Arrow/></em>
        </Link>
      );
    } else {
      cards.push(
        <article className="ses-related-card" key={`${title}-${i}`}>
          <span>{group}</span>
          <h3>{title}</h3>
          {desc && <p>{desc}</p>}
        </article>
      );
    }
  });

  return <div className="ses-related-grid">{cards}</div>;
};

const Page: React.FC = () => {
  const sections = config.sections as readonly (readonly string[])[];
  const hero = getHero(sections[0] || [], config.title);

  const lastSection = sections[sections.length - 1] || [];
  const lastRaw = lastSection.join("\n\n");
  const lastBlocks = splitBlocks(lastRaw).filter((x) => x !== "Primary CTA");
  const ctaLabel =
    lastBlocks.length && lastBlocks[lastBlocks.length - 1].length < 90
      ? lastBlocks[lastBlocks.length - 1]
      : "";
  const ctaContent = ctaLabel ? lastBlocks.slice(0, -1) : lastBlocks;
  const hasRelated = sections.length === 9;

  useEffect(() => {
    document.title = `${config.title} | Digicomm Semiconductor`;
  }, []);

  return (
    <Fragment>
      <div className={`specialized-service-page inner-page sta-redesign`}>
        <div className="body_wrap o-clip">
          <Header />

          <main>
            <PageTitle pageTitle={config.title} pagesub={config.title} />

            <section className="ses-hero">
              <div className="ses-shell ses-two-col">
                <div className="ses-hero__copy">
                  <h1>{hero.headline}</h1>
                  {hero.body.map((p, i) => <p key={i}>{p}</p>)}
                  {hero.cta && (
                    <Link className="ses-btn" to="/contact">
                      {hero.cta}<Arrow/>
                    </Link>
                  )}
                </div>
                <EngineeringVisual />
              </div>
            </section>

            {sections.slice(1, hasRelated ? -2 : -1).map((section, idx) => {
              const raw = section.join("\n\n");
              const blocks = splitBlocks(raw);
              const lead = blocks[0] || "";
              const firstLine = lead.split("\n")[0] || "";

              return (
                <section className={`ses-section ses-section--${idx + 2} ${idx % 2 ? "ses-section--alt" : ""}`} key={idx}>
                  <div className="ses-shell">
                    <div className="ses-section-head">
                      <span>{String(idx + 2).padStart(2, "0")}</span>
                      <h2>{firstLine}</h2>
                    </div>
                    <ContentPanel raw={raw} index={idx}/>
                  </div>
                </section>
              );
            })}

            {hasRelated && (
              <section className="ses-section ses-related">
                <div className="ses-shell">
                  <RelatedPanel raw={sections[sections.length - 2].join("\n\n")}/>
                </div>
              </section>
            )}

            <section className="ses-cta">
              <div className="ses-shell ses-two-col">
                <div className="ses-cta__copy">
                  {ctaContent.map((b, i) => {
                    const lines = b.split("\n").map((x) => x.trim()).filter(Boolean);
                    return i === 0
                      ? <h2 key={i}>{lines.join(" ")}</h2>
                      : <p key={i}>{lines.join(" ")}</p>;
                  })}

                  {ctaLabel && (
                    <Link className="ses-btn" to="/contact">
                      {ctaLabel}<Arrow/>
                    </Link>
                  )}
                </div>
                <EngineeringVisual />
              </div>
            </section>
          </main>

          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default Page;
